import React from "react"

import { AlertTriangle, Clock, Truck } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { formatDate } from "@/components/ui/formatDate"
import type { ActiveEngagements } from "@/hooks/useActiveEngagements"

interface RejectWarningDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  applicantName: string
  engagements: Pick<ActiveEngagements, "openTimesheets" | "upcomingShifts">
  onConfirm: () => void
  isConfirming?: boolean
}

const RejectWarningDialog: React.FC<RejectWarningDialogProps> = ({
  open,
  onOpenChange,
  applicantName,
  engagements,
  onConfirm,
  isConfirming = false,
}) => {
  const { openTimesheets, upcomingShifts } = engagements
  const openCount = openTimesheets.length
  const shiftCount = upcomingShifts.length

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Active engagements found
          </AlertDialogTitle>
          <AlertDialogDescription>
            {applicantName} still has active work on the system.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {openCount > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <Clock className="h-4 w-4 text-muted-foreground" />
                {openCount} open timesheet{openCount > 1 ? "s" : ""}
              </div>
              <ul className="pl-6 text-muted-foreground">
                {openTimesheets.slice(0, 5).map((timesheet) => (
                  <li key={timesheet.id}>
                    {timesheet.clocked_in_time ? `${formatDate(timesheet.clocked_in_time)}` : ""}
                    {timesheet.shift?.area ? ` · ${timesheet.shift.area}` : ""}
                    {timesheet.shift?.location ? ` · ${timesheet.shift.location}` : ""}
                  </li>
                ))}
                {openCount > 5 && <li>and {openCount - 5} more…</li>}
              </ul>
            </div>
          )}

          {shiftCount > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <Truck className="h-4 w-4 text-muted-foreground" />
                {shiftCount} upcoming block{shiftCount > 1 ? "s" : ""}
              </div>
              <ul className="pl-6 text-muted-foreground">
                {upcomingShifts.slice(0, 5).map((shift) => (
                  <li key={shift.id}>
                    {formatDate(shift.start_time)}
                    {shift.area ? ` · ${shift.area}` : ""}
                    {shift.location ? ` · ${shift.location}` : ""}
                  </li>
                ))}
                {shiftCount > 5 && <li>and {shiftCount - 5} more…</li>}
              </ul>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isConfirming}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isConfirming ? "Rejecting..." : "Reject anyway"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RejectWarningDialog
